import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SecurityIssuesList } from "@/components/security/SecurityIssuesList";
import { IssueSeverityCard } from "@/components/security/IssueSeverityCard";
import { IssueStatusTrendChart } from "@/components/security/IssueStatusTrendChart";
import { OpenIssuesTrendCard } from "@/components/security/OpenIssuesTrendCard";
import {
  mockSecurityIssues,
  mockStatusTrendData,
  mockIssueSeverityData,
  mockOpenIssueAgeData,
} from "@/data/mockSecurityIssues";
import { SeverityLevel, DateRange } from "@/types/security";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Filter, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export function IssuesPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>("30");
  const [severityFilter, setSeverityFilter] = useState<SeverityLevel[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  // Filter issues based on selected filters
  const filteredIssues = useMemo(() => {
    let filtered = [...mockSecurityIssues];

    if (severityFilter.length > 0) {
      filtered = filtered.filter((issue) => severityFilter.includes(issue.severity));
    }

    if (statusFilter.length > 0) {
      filtered = filtered.filter((issue) => statusFilter.includes(issue.status));
    }

    if (typeFilter.length > 0) {
      filtered = filtered.filter((issue) => typeFilter.includes(issue.type));
    }

    return filtered;
  }, [severityFilter, statusFilter, typeFilter]);

  const handleSeverityClick = (severity: string) => {
    setSeverityFilter([severity as SeverityLevel]);
  };

  const handleOpenIssuesClick = () => {
    setStatusFilter(["OPEN"]);
    setSeverityFilter(["CRITICAL"]);
  };

  const handleClearFilters = () => {
    setSeverityFilter([]);
    setStatusFilter([]);
    setTypeFilter([]);
  };

  const toggleSeverityFilter = (severity: SeverityLevel) => {
    setSeverityFilter((prev) =>
      prev.includes(severity) ? prev.filter((s) => s !== severity) : [...prev, severity]
    );
  };

  const toggleStatusFilter = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const toggleTypeFilter = (type: string) => {
    setTypeFilter((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const hasActiveFilters =
    severityFilter.length > 0 || statusFilter.length > 0 || typeFilter.length > 0;

  return (
    <div className="flex flex-1 flex-col gap-4 p-6">
      {/* Three cards side by side */}
      <div className="grid gap-4 md:grid-cols-3">
        <IssueSeverityCard data={mockIssueSeverityData} onSeverityClick={handleSeverityClick} />
        <IssueStatusTrendChart
          data={mockStatusTrendData}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
        <OpenIssuesTrendCard data={mockOpenIssueAgeData} onClick={handleOpenIssuesClick} />
      </div>

      {/* Security Issues Table with Filters */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base font-medium">
              Security Issues ({filteredIssues.length})
            </CardTitle>
            {hasActiveFilters && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {severityFilter.map((severity) => (
                  <Badge key={severity} variant="secondary" className="text-xs">
                    {severity}
                    <X
                      className="ml-1 h-3 w-3 cursor-pointer"
                      onClick={() => toggleSeverityFilter(severity)}
                    />
                  </Badge>
                ))}
                {statusFilter.map((status) => (
                  <Badge key={status} variant="secondary" className="text-xs">
                    {status}
                    <X
                      className="ml-1 h-3 w-3 cursor-pointer"
                      onClick={() => toggleStatusFilter(status)}
                    />
                  </Badge>
                ))}
                {typeFilter.map((type) => (
                  <Badge key={type} variant="secondary" className="text-xs">
                    {type}
                    <X
                      className="ml-1 h-3 w-3 cursor-pointer"
                      onClick={() => toggleTypeFilter(type)}
                    />
                  </Badge>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-6 px-2 text-xs"
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-sm font-semibold">Severity</div>
              <DropdownMenuCheckboxItem
                checked={severityFilter.includes("CRITICAL")}
                onCheckedChange={() => toggleSeverityFilter("CRITICAL")}
              >
                Critical
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={severityFilter.includes("HIGH")}
                onCheckedChange={() => toggleSeverityFilter("HIGH")}
              >
                High
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={severityFilter.includes("MEDIUM")}
                onCheckedChange={() => toggleSeverityFilter("MEDIUM")}
              >
                Medium
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={severityFilter.includes("LOW")}
                onCheckedChange={() => toggleSeverityFilter("LOW")}
              >
                Low
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-sm font-semibold">Status</div>
              <DropdownMenuCheckboxItem
                checked={statusFilter.includes("OPEN")}
                onCheckedChange={() => toggleStatusFilter("OPEN")}
              >
                Open
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilter.includes("IN_REVIEW")}
                onCheckedChange={() => toggleStatusFilter("IN_REVIEW")}
              >
                In Review
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilter.includes("RESOLVED")}
                onCheckedChange={() => toggleStatusFilter("RESOLVED")}
              >
                Resolved
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={statusFilter.includes("FALSE_POSITIVE")}
                onCheckedChange={() => toggleStatusFilter("FALSE_POSITIVE")}
              >
                False Positive
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-sm font-semibold">Type</div>
              <DropdownMenuCheckboxItem
                checked={typeFilter.includes("PII_DETECTION")}
                onCheckedChange={() => toggleTypeFilter("PII_DETECTION")}
              >
                PII Detection
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter.includes("PROMPT_INJECTION")}
                onCheckedChange={() => toggleTypeFilter("PROMPT_INJECTION")}
              >
                Prompt Injection
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter.includes("DATA_EXFILTRATION")}
                onCheckedChange={() => toggleTypeFilter("DATA_EXFILTRATION")}
              >
                Data Exfiltration
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter.includes("TOXIC_CONTENT")}
                onCheckedChange={() => toggleTypeFilter("TOXIC_CONTENT")}
              >
                Toxic Content
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter.includes("MALICIOUS_URL")}
                onCheckedChange={() => toggleTypeFilter("MALICIOUS_URL")}
              >
                Malicious URL
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={typeFilter.includes("SECRETS_EXPOSURE")}
                onCheckedChange={() => toggleTypeFilter("SECRETS_EXPOSURE")}
              >
                Secrets Exposure
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent className="pt-0">
          <SecurityIssuesList
            issues={filteredIssues}
            onIssueClick={(issue) => navigate(`/issues/${issue.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
